import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Link,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { Cog6ToothIcon, LightBulbIcon } from "@heroicons/react/24/outline";

import { setCurrentProject, setProjects } from "../redux/project/projectSlice";
import { changeRequestsService } from "../services/changeRequests";
import { giftsService } from "../services/gifts";
import { projectPlansService } from "../services/projectPlans";
import { projectsService } from "../services/projects";
import { tablesService } from "../services/tables";
import { useToast } from "../contexts/ToastContext";
import { getApiErrorMessage } from "../utils/axios";
import { handleBillingError } from "../utils/billing";
import { currentAttempt } from "../utils/agentLog";
import { sitePath } from "../utils/urls";
import AgentLog from "../components/common/AgentLog";
import FolderTabs from "../components/common/FolderTabs";
import PremiumModelPicker from "../components/common/PremiumModelPicker";
import WeaveLoader from "../components/common/WeaveLoader";
import Logo from "../components/Logo";
import BuilderField from "../components/onboarding/BuilderField";
import Intro from "../components/onboarding/Intro";

const STEPS = [
  { label: "Intro", path: "/onboarding/" },
  { label: "Brief", path: "/onboarding/brief/" },
  { label: "Plan", path: "/onboarding/plan/" },
  { label: "Generate", path: "/onboarding/generating/" },
];

const ANALYSIS_STEPS_WITH_TABLES = [
  ["Saving your tables", "Preparing each dataset for analysis"],
  ["Reading representative samples", "Finding useful entities, fields, and relationships"],
  ["Planning pages and specs", "Matching the data to search intent and site guidance"],
  ["Drafting site specs", "Writing the strategy for content, data, and design"],
];

const ANALYSIS_STEPS_BRIEF_ONLY = [
  ["Researching the space", "Understanding the niche, audience, and competitive landscape"],
  ["Compiling possible data sources", "Finding datasets and APIs that could power the site"],
  ["Planning pages and specs", "Matching search intent to a useful site structure"],
  ["Drafting site specs", "Writing the strategy for content, data, and design"],
];

const ANALYSIS_STEP_BASE_MS = 4500;

const BRIEF_EXAMPLES = [
  {
    id: "baby-names",
    title: "Baby Names",
    prompt:
      "Build a baby name explorer for parents. Each name gets a page with meaning, origin, gender usage, and popularity over time. Include ranked lists by year and decade, plus comparison pages for similar names. Keep the tone warm and practical.",
  },
  {
    id: "pet-stays",
    title: "Pet Stays",
    prompt:
      "Build a directory of dog-friendly hotels and stays. Every listing page should cover pet policies, fees, nearby parks, and neighborhood vibe. Add city and neighborhood index pages so travelers can browse by place. Helpful and local, not salesy.",
  },
  {
    id: "gear-compare",
    title: "Gear Compare",
    prompt:
      "Build a comparison site for outdoor gear under clear price bands. Each product page should highlight specs, who it is for, and tradeoffs. Include category ranking pages and head-to-head comparisons when the data supports them. Straight, opinionated, and useful for buyers deciding between options.",
  },
];

const BRIEF_EXAMPLE_PROMPTS = new Set(BRIEF_EXAMPLES.map((example) => example.prompt));

const CSV_PANEL_HEIGHT = "h-44";

const DEFAULT_SPEC_FILES = [
  { filename: "SITE.md", empty_comment: "<!-- Add site identity, audience, and principles here. -->" },
  { filename: "DATA.md", empty_comment: "<!-- Add dataset roles, entities, and data pipeline notes here. -->" },
  { filename: "CONTENT.md", empty_comment: "<!-- Add voice, sourcing rules, and page composition notes here. -->" },
  { filename: "DESIGN.md", empty_comment: "<!-- Add visual direction and design cues here. -->" },
];

const loadSiteSpecFiles = () => {
  try {
    const config = JSON.parse(localStorage.getItem("currentUser") || "{}");
    const files = config.env?.site_spec_files;
    if (Array.isArray(files) && files.length) {
      return files;
    }
  } catch (_) {}
  return DEFAULT_SPEC_FILES;
};

const emptySpecsFromRegistry = (registry) =>
  Object.fromEntries((registry || DEFAULT_SPEC_FILES).map((item) => [item.filename, ""]));

const normalizeSpecs = (raw, registry) => {
  const files = registry || DEFAULT_SPEC_FILES;
  const source = raw && typeof raw === "object" ? raw : {};
  const next = {};
  files.forEach((item) => {
    next[item.filename] = typeof source[item.filename] === "string" ? source[item.filename] : "";
  });
  return next;
};

const stepFromPath = (pathname) => {
  if (pathname.startsWith("/onboarding/generating") || pathname.startsWith("/onboarding/complete")) return 4;
  if (pathname.startsWith("/onboarding/plan")) return 3;
  if (pathname.startsWith("/onboarding/brief")) return 2;
  return 1;
};

const OnboardingLayout = () => {
  const step = stepFromPath(useLocation().pathname);

  return (
    <div className="relative h-full min-h-screen overflow-y-auto bg-theme-secondary px-4 py-10 text-theme-text">
      <BuilderField className="pointer-events-none fixed inset-0 h-full w-full opacity-30" />
      <Link
        to="/"
        className="absolute left-6 top-6 z-10 hidden items-center gap-2 sm:flex"
      >
        <Logo className="h-5 w-[52px]" />
        <span className="text-[14.5px] font-bold">Weave</span>
      </Link>
      <div className="relative mx-auto max-w-3xl rounded-2xl border border-theme-secondary bg-theme-main p-6 shadow-xl sm:p-12">
        <div className="mb-9 flex items-center justify-center gap-2.5 text-[12.5px] font-semibold">
          {STEPS.map((item, index) => {
            const stepNumber = index + 1;
            const isCurrent = step === stepNumber;
            const isPast = step > stepNumber;
            const content = (
              <>
                <span
                  className={`inline-flex h-[22px] w-[22px] items-center justify-center rounded-full text-[11px] ${
                    isPast
                      ? "bg-theme-secondary text-theme-primary"
                      : isCurrent
                        ? "bg-theme-primary text-theme-button"
                        : "border-[1.5px] border-theme-secondary"
                  }`}
                >
                  {isPast ? "✓" : stepNumber}
                </span>
                <span className="hidden sm:inline">{item.label}</span>
              </>
            );
            return (
              <React.Fragment key={item.label}>
                {index > 0 && <div className="mt-[11px] h-px w-9 self-start bg-theme-secondary" />}
                {isPast ? (
                  <Link
                    to={item.path}
                    state={{ fromStepper: true }}
                    className="flex items-center gap-1.5 opacity-50 transition hover:opacity-100"
                  >
                    {content}
                  </Link>
                ) : (
                  <div className={`flex items-center gap-1.5 ${isCurrent ? "text-theme-text" : "opacity-50"}`}>
                    {content}
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
        <Outlet />
      </div>
    </div>
  );
};

const RequireOnboardingDraft = () => {
  const project = useSelector((state) => state.project.currentProject);
  const [ready, setReady] = useState(false);
  const [hasDraft, setHasDraft] = useState(false);

  useEffect(() => {
    projectPlansService.getCurrent(
      (response) => {
        const plan = response.data;
        const fromPlan = plan?.site_plan && Object.keys(plan.site_plan).length > 0;
        const fromProject = project?.site_plan && Object.keys(project.site_plan).length > 0;
        setHasDraft(Boolean(fromPlan || fromProject));
        setReady(true);
      },
      () => {
        const fromProject = project?.site_plan && Object.keys(project.site_plan).length > 0;
        setHasDraft(Boolean(fromProject));
        setReady(true);
      }
    );
  }, [project?.site_plan]);

  if (!ready) {
    return (
      <div className="flex justify-center py-24">
        <WeaveLoader size={64} />
      </div>
    );
  }
  if (!hasDraft) return <Navigate to="/onboarding/brief/" replace />;
  return <Outlet />;
};

const RequireOnboardingProject = () => {
  const { currentProject, loaded } = useSelector((state) => state.project);
  if (!loaded) {
    return (
      <div className="flex justify-center py-24">
        <WeaveLoader size={64} />
      </div>
    );
  }
  if (!currentProject) return <Navigate to="/onboarding/brief/" replace />;
  return <Outlet />;
};

const BriefStep = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { error: toastError } = useToast();
  const [checkingPlan, setCheckingPlan] = useState(true);
  const [gift, setGift] = useState(null);
  const [respondingToGift, setRespondingToGift] = useState(false);
  const [uploads, setUploads] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [siteBrief, setSiteBrief] = useState("");
  const [suggestingBrief, setSuggestingBrief] = useState(false);
  const [error, setError] = useState("");
  const [importing, setImporting] = useState(false);
  const [analysisStep, setAnalysisStep] = useState(0);
  const [planSettingsOpen, setPlanSettingsOpen] = useState(false);
  const [examplesOpen, setExamplesOpen] = useState(false);
  const [preApprove, setPreApprove] = useState(false);
  const [autoPublish, setAutoPublish] = useState(false);
  const siteBriefRef = useRef("");
  const fileInputRef = useRef(null);
  const planSettingsRef = useRef(null);
  const examplesRef = useRef(null);

  const selectedUpload = uploads.find((item) => item.id === selectedId) || uploads[0];
  const csvPreview = selectedUpload?.preview;
  const previewRows = csvPreview?.data || csvPreview?.preview || [];
  const hasTables = uploads.length > 0;
  const truncatedUploads = uploads.filter(
    (item) => item.preview?.truncated && item.preview?.source_row_count > item.preview?.row_count
  );
  const analysisSteps = hasTables ? ANALYSIS_STEPS_WITH_TABLES : ANALYSIS_STEPS_BRIEF_ONLY;
  const continueLabel = importing ? "Designing your plan…" : "Continue";

  const updateSiteBrief = (value) => {
    siteBriefRef.current = value;
    setSiteBrief(value);
  };

  useEffect(() => {
    // Stay on Brief when the user clicked back via the stepper.
    if (location.state?.fromStepper) {
      setCheckingPlan(false);
      return;
    }
    const checkDraftPlan = () => projectPlansService.getCurrent(
      (response) => {
        if (response.data?.site_plan && Object.keys(response.data.site_plan).length > 0) {
          navigate("/onboarding/plan/", { replace: true });
          return;
        }
        setCheckingPlan(false);
      },
      () => setCheckingPlan(false)
    );
    // A gift from signup that hasn't been used or declined takes over this step.
    giftsService.getPending(
      (response) => {
        if (response.data?.id) {
          setGift(response.data);
          setCheckingPlan(false);
          return;
        }
        checkDraftPlan();
      },
      checkDraftPlan
    );
  }, [navigate, location.state]);

  const respondToGift = (accepted) => {
    setRespondingToGift(true);
    giftsService.respondPending(
      accepted,
      () => {
        setRespondingToGift(false);
        if (!accepted) {
          setGift(null);
          return;
        }
        navigate(gift.gift_type === "site" ? sitePath(gift.site_slug, "preview/") : "/onboarding/plan/");
      },
      (requestError) => {
        setRespondingToGift(false);
        toastError(getApiErrorMessage(requestError, "Could not update your gift."));
      }
    );
  };

  useEffect(() => {
    if (!importing) return undefined;
    const timeouts = [];
    let elapsed = 0;
    let delay = ANALYSIS_STEP_BASE_MS;
    for (let step = 1; step < analysisSteps.length; step++) {
      elapsed += delay;
      timeouts.push(setTimeout(() => setAnalysisStep(step), elapsed));
      delay *= 2;
    }
    return () => timeouts.forEach(clearTimeout);
  }, [importing, analysisSteps.length]);

  const requestBriefSuggestion = (items) => {
    const current = siteBriefRef.current.trim();
    const canOverwrite = !current || BRIEF_EXAMPLE_PROMPTS.has(current);
    if (!items.length || !canOverwrite) return;
    setSuggestingBrief(true);
    projectPlansService.suggestBrief(
      items.map((item) => ({
        name: item.preview.name,
        schema: item.preview.schema,
        sample_rows: (item.preview.preview || item.preview.data || []).slice(0, 3),
      })),
      (response) => {
        setSuggestingBrief(false);
        const brief = (response.data?.brief || "").trim();
        const stillCurrent = siteBriefRef.current.trim();
        if (brief && (!stillCurrent || BRIEF_EXAMPLE_PROMPTS.has(stillCurrent))) {
          updateSiteBrief(brief);
        }
      },
      () => setSuggestingBrief(false)
    );
  };

  const uploadCsv = (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    setError("");
    event.target.value = "";

    let pending = files.length;
    const finishBatch = () => {
      pending -= 1;
      if (pending > 0) return;
      setUploads((items) => {
        requestBriefSuggestion(items);
        return items;
      });
    };

    files.forEach((file) => {
      tablesService.uploadCsv(
        null,
        file,
        (response) => {
          const id = `${response.data.name}-${Date.now()}-${Math.random()}`;
          setUploads((items) => [...items, { id, preview: response.data }]);
          setSelectedId((current) => current || id);
          finishBatch();
        },
        (requestError) => {
          setError(getApiErrorMessage(requestError, "Could not read this CSV."));
          finishBatch();
        }
      );
    });
  };

  const removeUpload = (id) => {
    const next = uploads.filter((item) => item.id !== id);
    setUploads(next);
    if (selectedId === id) {
      setSelectedId(next[0]?.id || null);
    }
    setError("");
  };

  useEffect(() => {
    if (!planSettingsOpen) return undefined;
    const onDoc = (event) => {
      if (planSettingsRef.current && !planSettingsRef.current.contains(event.target)) {
        setPlanSettingsOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [planSettingsOpen]);

  useEffect(() => {
    if (!examplesOpen) return undefined;
    const onDoc = (event) => {
      if (examplesRef.current && !examplesRef.current.contains(event.target)) {
        setExamplesOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [examplesOpen]);

  const handleAnalyzeSuccess = (response) => {
    // Pre-approve returns a project (has slug); otherwise a draft plan.
    if (response.data?.slug) {
      dispatch(setCurrentProject(response.data));
      navigate("/onboarding/generating/");
      return;
    }
    navigate("/onboarding/plan/");
  };

  const confirmCsv = () => {
    const brief = siteBrief.trim();
    if (!uploads.length && !brief) return;
    setAnalysisStep(0);
    setImporting(true);
    setError("");
    const analyzeOptions = { pre_approve: preApprove, auto_publish: autoPublish };

    if (!uploads.length) {
        projectPlansService.analyze(
        [],
        brief,
        handleAnalyzeSuccess,
        (requestError) => {
          setImporting(false);
          if (handleBillingError(requestError, toastError)) return;
          setError(getApiErrorMessage(requestError, "Could not analyze this brief."));
        },
        analyzeOptions,
      );
      return;
    }

    const usedNames = new Set();
    const tableHashes = [];

    const createNext = (index) => {
      if (index >= uploads.length) {
        projectPlansService.analyze(
          tableHashes,
          brief,
          handleAnalyzeSuccess,
          (requestError) => {
            setImporting(false);
            if (handleBillingError(requestError, toastError)) return;
            setError(getApiErrorMessage(requestError, "Could not analyze this data."));
          },
          analyzeOptions,
        );
        return;
      }
      const upload = uploads[index];
      let name = upload.preview.name.toLowerCase().replace(/[^a-z0-9]+/g, "_");
      if (usedNames.has(name)) {
        let suffix = 2;
        while (usedNames.has(`${name}_${suffix}`)) suffix += 1;
        name = `${name}_${suffix}`;
      }
      usedNames.add(name);

      tablesService.create(
        null,
        {
          name,
          source: "csv_upload",
          schema: upload.preview.schema,
          data: upload.preview.data,
          source_row_count: upload.preview.source_row_count || upload.preview.row_count,
        },
        (response) => {
          tableHashes.push(response.data.hash);
          createNext(index + 1);
        },
        (requestError) => {
          setImporting(false);
          setError(getApiErrorMessage(requestError, "Could not import this table."));
        }
      );
    };

    createNext(0);
  };

  if (checkingPlan) {
    return (
      <div className="flex justify-center py-24">
        <WeaveLoader size={64} />
      </div>
    );
  }

  if (importing) return <AnalysisLoader activeStep={analysisStep} hasTables={hasTables} />;

  if (gift) return <GiftChoice gift={gift} busy={respondingToGift} onRespond={respondToGift} />;

  return (
    <>
      {error && <ErrorBanner>{error}</ErrorBanner>}
      <div>
        <h1 className="text-2xl font-bold">Add your data</h1>
        <p className="mt-2 text-[15px] leading-6 opacity-70">
          Weave works best when you upload data to start from, like a product list, reviews, places, or
          stats. Uploading is optional. If you don't have a dataset, describe the site you want below and
          Weave will research the space, compile the data, and build it.
        </p>

        {truncatedUploads.length > 0 && (
          <div className="mt-5 rounded-lg border border-theme-secondary bg-theme-secondary/40 p-3 text-sm">
            <p className="font-medium">Some CSV files are too large to import in full.</p>
            <ul className="mt-2 list-disc space-y-1 pl-5 opacity-80">
              {truncatedUploads.map((item) => (
                <li key={item.id}>
                  {item.preview.name}: using the first{" "}
                  {Number(item.preview.row_count).toLocaleString()} of{" "}
                  {Number(item.preview.source_row_count).toLocaleString()} rows
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-6">
          <div className="mb-2 flex items-center justify-between gap-3">
            <h2 className="text-sm font-semibold">CSV files</h2>
            {uploads.length > 0 && (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="text-xs font-semibold text-theme-primary hover:opacity-80"
              >
                + Add more
              </button>
            )}
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            multiple
            onChange={uploadCsv}
            className="hidden"
          />

          {uploads.length === 0 ? (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className={`flex w-full ${CSV_PANEL_HEIGHT} flex-col items-center justify-center rounded-xl border-2 border-dashed border-theme-secondary bg-theme-secondary/40 px-6 text-center transition hover:border-theme-primary hover:bg-theme-secondary/70`}
            >
              <CsvEmptyArt />
              <strong className="mt-2 text-sm">Drop CSV files here, or click to browse</strong>
              <p className="mt-1 max-w-sm text-xs leading-5 opacity-60">
                Listings, products, places, stats. Multiple files are fine.
              </p>
            </button>
          ) : (
            <div className={`flex ${CSV_PANEL_HEIGHT} flex-col overflow-hidden rounded-xl border border-theme-secondary`}>
              <div className="flex shrink-0 items-center gap-1 overflow-x-auto border-b border-theme-secondary bg-theme-secondary/50 px-2 pt-2">
                {uploads.map((upload) => {
                  const active = selectedUpload?.id === upload.id;
                  return (
                    <div
                      key={upload.id}
                      className={`group flex shrink-0 items-center gap-1 rounded-t-lg border border-b-0 px-2.5 py-1.5 text-xs ${
                        active
                          ? "border-theme-secondary bg-theme-main font-semibold"
                          : "border-transparent opacity-60 hover:opacity-100"
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() => setSelectedId(upload.id)}
                        className="max-w-[9rem] truncate"
                      >
                        {upload.preview.name}
                      </button>
                      <button
                        type="button"
                        onClick={() => removeUpload(upload.id)}
                        aria-label={`Remove ${upload.preview.name}`}
                        className="rounded px-1 opacity-40 hover:bg-theme-secondary hover:opacity-100"
                      >
                        ×
                      </button>
                    </div>
                  );
                })}
              </div>
              {csvPreview && (
                <div className="min-h-0 flex-1 overflow-auto">
                  <table className="w-full min-w-max border-collapse text-left text-xs">
                    <thead>
                      <tr className="sticky top-0 z-[1] border-b border-theme-secondary bg-theme-main/90 backdrop-blur-sm">
                        {csvPreview.schema.map((column) => (
                          <th key={column.name} className="bg-theme-main/90 px-3 py-2 font-semibold whitespace-nowrap backdrop-blur-sm">
                            {column.name}{" "}
                            <span className="font-normal opacity-40">{column.type}</span>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {previewRows.map((row, index) => (
                        <tr key={index} className="border-b border-theme-secondary/60">
                          {csvPreview.schema.map((column) => (
                            <td key={column.name} className="max-w-[14rem] truncate px-3 py-1.5 opacity-80">
                              {row[column.name] ?? ""}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="mt-8">
          <div className="mb-2 flex items-end justify-between gap-3">
            <div>
              <h2 className="text-sm font-semibold">Site brief</h2>
              <p className="mt-0.5 text-xs">
                {uploads.length > 0 ? (
                  <span className="opacity-55">
                    {suggestingBrief
                      ? "Drafting a brief from your columns…"
                      : "Optional. Edit the suggestion or write your own."}
                  </span>
                ) : (
                  <>
                    <span className="opacity-55">
                      Describe the site you want. Required if you skip the CSV. Not sure what to
                      build?{" "}
                    </span>
                    <span className="relative inline" ref={examplesRef}>
                      <button
                        type="button"
                        onClick={() => setExamplesOpen((open) => !open)}
                        className="underline underline-offset-2 opacity-55 hover:opacity-80"
                      >
                        Try an example
                      </button>
                      {examplesOpen && (
                        <div className="absolute left-0 top-full z-20 mt-2 w-56 rounded-xl border border-theme-secondary bg-theme-main p-1.5 text-theme-text shadow-lg">
                          {BRIEF_EXAMPLES.map((example) => (
                            <button
                              key={example.id}
                              type="button"
                              onClick={() => {
                                updateSiteBrief(example.prompt);
                                setExamplesOpen(false);
                              }}
                              className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-xs font-medium hover:bg-theme-secondary"
                            >
                              <LightBulbIcon className="h-3.5 w-3.5 shrink-0 opacity-60" />
                              {example.title}
                            </button>
                          ))}
                        </div>
                      )}
                    </span>
                  </>
                )}
              </p>
            </div>
          </div>
          <textarea
            value={siteBrief}
            onChange={(event) => updateSiteBrief(event.target.value)}
            rows={6}
            className="block w-full rounded-xl border border-theme-secondary bg-theme-secondary/20 px-4 py-3 text-sm leading-6 outline-none transition focus:border-theme-primary focus:bg-theme-main"
            placeholder="Audience, how to treat the data, page structure, editorial rules, SEO focus, design cues…"
          />
        </div>

        <div className="relative mt-6 flex items-center gap-2" ref={planSettingsRef}>
          <button
            type="button"
            onClick={() => setPlanSettingsOpen((open) => !open)}
            className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-lg border border-theme-secondary opacity-70 hover:opacity-100"
            aria-label="Plan settings"
          >
            <Cog6ToothIcon className="h-5 w-5" />
          </button>
          {planSettingsOpen && (
            <div className="absolute bottom-full left-0 z-20 mb-2 w-72 rounded-xl border border-theme-secondary bg-theme-main p-4 shadow-lg">
              <h3 className="text-sm font-semibold">Plan settings</h3>
              <label className="mt-3 flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={preApprove}
                  onChange={(e) => setPreApprove(e.target.checked)}
                />
                <span>
                  <span className="font-medium">Pre-approve</span>
                  <span className="mt-0.5 block text-xs opacity-60">
                    The build starts as soon as the plan is ready.
                  </span>
                </span>
              </label>
              <label className="mt-3 flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={autoPublish}
                  onChange={(e) => setAutoPublish(e.target.checked)}
                />
                <span>
                  <span className="font-medium">Auto-publish</span>
                  <span className="mt-0.5 block text-xs opacity-60">
                    When the sitebuilder change is created, publish it when finished so live_url is stable.
                  </span>
                </span>
              </label>
            </div>
          )}
          <button
            type="button"
            disabled={(!uploads.length && !siteBrief.trim()) || importing}
            onClick={confirmCsv}
            className="min-w-0 flex-1 rounded-lg bg-theme-primary py-3 font-semibold text-theme-button disabled:opacity-50"
          >
            {continueLabel}
          </button>
        </div>
      </div>
    </>
  );
};

const PlanStep = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { error: toastError } = useToast();
  const project = useSelector((state) => state.project.currentProject);
  const projects = useSelector((state) => state.project.projects);
  const [draftPlan, setDraftPlan] = useState(null);
  const [loadingPlan, setLoadingPlan] = useState(true);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [subdomain, setSubdomain] = useState("");
  const [subdomainStatus, setSubdomainStatus] = useState("idle");
  const [subdomainAvailable, setSubdomainAvailable] = useState(null);
  const lastCheckedSubdomain = useRef("");
  const subdomainCheckTimer = useRef(null);
  const [pageTemplates, setPageTemplates] = useState([]);
  const [selectedTemplateIndex, setSelectedTemplateIndex] = useState(0);
  const [specFiles, setSpecFiles] = useState(DEFAULT_SPEC_FILES);
  const [specs, setSpecs] = useState(() => emptySpecsFromRegistry(DEFAULT_SPEC_FILES));
  const [activeSpec, setActiveSpec] = useState(DEFAULT_SPEC_FILES[0].filename);
  const [error, setError] = useState("");
  const [generating, setGenerating] = useState(false);
  const [pseoDomain, setPseoDomain] = useState("dock.md");
  const [isStaff, setIsStaff] = useState(false);
  const [modelOverride, setModelOverride] = useState("");
  const selectedTemplate = pageTemplates[selectedTemplateIndex];
  const activeSpecMeta = specFiles.find((item) => item.filename === activeSpec) || specFiles[0];
  const activeSpecContent = specs[activeSpec] || "";

  useEffect(() => {
    try {
      const config = JSON.parse(localStorage.getItem("currentUser") || "{}");
      if (config.env?.domain) setPseoDomain(config.env.domain);
      setIsStaff(Boolean(config.identity?.is_staff));
      const registry = loadSiteSpecFiles();
      setSpecFiles(registry);
      setActiveSpec(registry[0]?.filename || "SITE.md");
    } catch (_) {}
  }, []);

  useEffect(() => {
    projectPlansService.getCurrent(
      (response) => {
        const plan = response.data;
        if (plan?.hash) {
          setDraftPlan(plan);
          applyPlanToForm(plan.site_plan || {}, plan.subdomain || "");
        } else if (project?.site_plan) {
          applyPlanToForm(project.site_plan, project.subdomain || "");
        }
        setLoadingPlan(false);
      },
      () => {
        if (project?.site_plan) {
          applyPlanToForm(project.site_plan, project.subdomain || "");
        }
        setLoadingPlan(false);
      }
    );

    function applyPlanToForm(sitePlan, nextSubdomain) {
      const registry = loadSiteSpecFiles();
      setSpecFiles(registry);
      setName(sitePlan.name || "");
      setDescription(sitePlan.description || "");
      setSubdomain(nextSubdomain);
      setSubdomainStatus(nextSubdomain ? "done" : "idle");
      setSubdomainAvailable(nextSubdomain ? true : null);
      lastCheckedSubdomain.current = nextSubdomain;
      setPageTemplates(sitePlan.page_templates || []);
      setSelectedTemplateIndex(0);
      const nextSpecs = normalizeSpecs(sitePlan.specs, registry);
      setSpecs(nextSpecs);
      setActiveSpec(registry[0]?.filename || "SITE.md");
    }
  }, [project?.hash, project?.site_plan, project?.subdomain]);

  const runSubdomainCheck = (value) => {
    const trimmed = value.trim();
    if (!trimmed) {
      setSubdomainStatus("idle");
      setSubdomainAvailable(null);
      lastCheckedSubdomain.current = "";
      return;
    }
    setSubdomainStatus("checking");
    setSubdomainAvailable(null);
    projectsService.checkSubdomain(
      trimmed,
      project?.hash || null,
      (response) => {
        lastCheckedSubdomain.current = response.data.subdomain || trimmed;
        setSubdomainStatus("done");
        setSubdomainAvailable(response.data.available);
      },
      () => {
        setSubdomainStatus("done");
        setSubdomainAvailable(false);
      }
    );
  };

  const handleSubdomainChange = (event) => {
    const value = event.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "");
    setSubdomain(value);
    if (subdomainCheckTimer.current) {
      clearTimeout(subdomainCheckTimer.current);
    }
    if (value.trim() !== lastCheckedSubdomain.current) {
      setSubdomainAvailable(null);
      setSubdomainStatus("idle");
    }
    if (value.trim() && value.trim() !== lastCheckedSubdomain.current) {
      subdomainCheckTimer.current = setTimeout(() => runSubdomainCheck(value), 1000);
    }
  };

  const handleSubdomainBlur = () => {
    if (subdomainCheckTimer.current) {
      clearTimeout(subdomainCheckTimer.current);
      subdomainCheckTimer.current = null;
    }
    if (subdomain.trim() && subdomain.trim() !== lastCheckedSubdomain.current) {
      runSubdomainCheck(subdomain);
    }
  };

  useEffect(() => () => {
    if (subdomainCheckTimer.current) {
      clearTimeout(subdomainCheckTimer.current);
    }
  }, []);

  const patchTemplate = (index, patch) => {
    setPageTemplates((items) => items.map((template, itemIndex) => (
      itemIndex === index ? { ...template, ...patch } : template
    )));
  };

  const startGeneration = (projectHash) => {
    const payload = { brief: description.trim() || "Initial site build" };
    if (isStaff) {
      payload.model_override = modelOverride;
    }
    projectsService.generateProject(
      projectHash,
      payload,
      (response) => {
        dispatch(setCurrentProject(response.data));
        navigate("/onboarding/generating/");
      },
      (requestError) => {
        setGenerating(false);
        setError(getApiErrorMessage(requestError, "Could not start site generation."));
      }
    );
  };

  const startOver = () => {
    if (!draftPlan?.hash) return;
    projectPlansService.reject(
      draftPlan.hash,
      () => navigate("/onboarding/brief/"),
      (err) => toastError(getApiErrorMessage(err, "Could not start over.")),
    );
  };

  const generate = () => {
    setGenerating(true);
    setError("");
    const sitePlan = {
      name: name.trim(),
      description: description.trim(),
      page_templates: pageTemplates,
      specs: normalizeSpecs(specs, specFiles),
    };
    if (draftPlan?.hash) {
      projectPlansService.approve(
        draftPlan.hash,
        { site_plan: sitePlan, subdomain },
        (response) => {
          const created = response.data;
          dispatch(setCurrentProject(created));
          dispatch(setProjects([created, ...projects.filter((item) => item.hash !== created.hash)]));
          startGeneration(created.hash);
        },
        (requestError) => {
          setGenerating(false);
          if (handleBillingError(requestError, toastError)) return;
          setError(getApiErrorMessage(requestError, "Could not create this site."));
        }
      );
      return;
    }
    if (project?.hash) {
      projectsService.updateProject(
        project.hash,
        {
          name: sitePlan.name,
          description: sitePlan.description,
          slug: subdomain,
          subdomain,
          site_plan: sitePlan,
        },
        () => startGeneration(project.hash),
        (requestError) => {
          setGenerating(false);
          setError(getApiErrorMessage(requestError, "Could not save these site details."));
        }
      );
    }
  };

  if (loadingPlan) {
    return (
      <div className="flex justify-center py-24">
        <WeaveLoader size={64} />
      </div>
    );
  }

  const specTabs = specFiles.map((item) => ({ id: item.filename, label: item.filename }));
  const templateTabs = pageTemplates.map((item, index) => ({
    id: String(index),
    label: item.name || `Page ${index + 1}`,
  }));

  return (
    <>
      {error && <ErrorBanner>{error}</ErrorBanner>}
      <div>
        <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold">Review your site plan</h1>
            <p className="mt-2 text-sm opacity-60">
              Here's a draft of your site. Check the details, specs, and pages, then generate when you're ready.
            </p>
          </div>
          {draftPlan?.hash && (
            <button
              type="button"
              className="text-sm font-semibold text-theme-primary"
              onClick={startOver}
            >
              Start over
            </button>
          )}
        </div>

        {(draftPlan?.truncated_tables || []).length > 0 && (
          <div className="mb-6 rounded-lg border border-theme-secondary bg-theme-secondary/40 p-3 text-sm">
            <p className="font-medium">Some CSV files were too large to import in full.</p>
            <ul className="mt-2 list-disc space-y-1 pl-5 opacity-80">
              {(draftPlan.truncated_tables || []).map((table) => (
                <li key={table.name}>
                  {table.name}: using the first {Number(table.row_count).toLocaleString()} of{" "}
                  {Number(table.source_row_count).toLocaleString()} rows
                </li>
              ))}
            </ul>
          </div>
        )}

        <section className="mb-8">
          <h2 className="mb-4 text-sm font-semibold">Details</h2>
          <label className="mb-5 block text-sm font-medium">
            Site name
            <input required value={name} onChange={(event) => setName(event.target.value)} className="mt-2 block w-full rounded-lg border border-theme-secondary px-4 py-3" />
          </label>
          <label className="mb-5 block text-sm font-medium">
            Subdomain
            <div
              className={`mt-2 flex items-center rounded-lg border bg-theme-main ${
                subdomainStatus === "done" && subdomainAvailable === false
                  ? "border-red-500"
                  : "border-theme-secondary"
              }`}
            >
              <input
                required
                disabled={subdomainStatus === "checking"}
                value={subdomain}
                onChange={handleSubdomainChange}
                onBlur={handleSubdomainBlur}
                className="min-w-0 flex-1 border-0 bg-transparent px-4 py-3 disabled:opacity-60"
              />
              {subdomainStatus === "checking" && (
                <span
                  aria-label="Checking availability"
                  className="mr-3 inline-block h-4 w-4 animate-spin rounded-full border-2 border-theme-secondary border-t-theme-primary"
                />
              )}
              {subdomainStatus === "done" && subdomainAvailable && (
                <span className="mr-3 text-green-600" aria-label="Available">✓</span>
              )}
              {subdomainStatus === "done" && subdomainAvailable === false && (
                <span className="mr-3 text-red-500" aria-label="Taken">✗</span>
              )}
              <span className="pr-4 text-sm opacity-50">.{pseoDomain}</span>
            </div>
            {subdomainStatus === "done" && subdomainAvailable === false && (
              <p className="mt-2 text-xs text-red-500">This subdomain is already in use.</p>
            )}
          </label>
          {isStaff && (
            <div>
              <p className="mb-2 text-sm font-medium">Model</p>
              <PremiumModelPicker
                value={modelOverride}
                onChange={setModelOverride}
              />
            </div>
          )}
        </section>

        <section className="mb-8">
          <h2 className="mb-4 text-sm font-semibold">Site specs</h2>
          <FolderTabs tabs={specTabs} activeId={activeSpec} onChange={setActiveSpec}>
            <textarea
              value={activeSpecContent}
              onChange={(event) => setSpecs((current) => ({
                ...current,
                [activeSpec]: event.target.value,
              }))}
              rows={14}
              placeholder={activeSpecMeta?.empty_comment || "<!-- Add details here. -->"}
              className="block w-full rounded-lg border border-theme-secondary bg-theme-main px-4 py-3 font-mono text-xs leading-5"
            />
          </FolderTabs>
        </section>

        <section className="mb-8">
          <h2 className="mb-4 text-sm font-semibold">Templates</h2>
          {pageTemplates.length === 0 ? (
            <p className="text-sm opacity-60">No page templates yet.</p>
          ) : (
            <FolderTabs
              tabs={templateTabs}
              activeId={String(selectedTemplateIndex)}
              onChange={(id) => setSelectedTemplateIndex(Number(id))}
            >
              {selectedTemplate && (
                <div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <label className="block text-sm font-medium">
                      Name
                      <input
                        value={selectedTemplate.name}
                        onChange={(event) => patchTemplate(selectedTemplateIndex, { name: event.target.value })}
                        className="mt-2 block w-full rounded-lg border border-theme-secondary px-3 py-2 text-sm font-normal"
                      />
                    </label>
                    <label className="block text-sm font-medium">
                      Route
                      <input
                        value={selectedTemplate.route}
                        onChange={(event) => patchTemplate(selectedTemplateIndex, { route: event.target.value })}
                        className="mt-2 block w-full rounded-lg border border-theme-secondary px-3 py-2 text-sm font-normal"
                        placeholder="/route/[slug]/"
                      />
                    </label>
                  </div>
                  <label className="mt-3 block text-sm font-medium">
                    Purpose
                    <input
                      value={selectedTemplate.purpose}
                      onChange={(event) => patchTemplate(selectedTemplateIndex, { purpose: event.target.value })}
                      className="mt-2 block w-full rounded-lg border border-theme-secondary px-3 py-2 text-sm font-normal"
                    />
                  </label>
                  <label className="mt-3 block text-sm font-medium">
                    Entity
                    <input
                      value={selectedTemplate.entity || ""}
                      onChange={(event) => patchTemplate(selectedTemplateIndex, { entity: event.target.value })}
                      className="mt-2 block w-full rounded-lg border border-theme-secondary px-3 py-2 text-sm font-normal"
                      placeholder="What one page represents (book, author)"
                    />
                  </label>
                  <label className="mt-3 block text-sm font-medium">
                    Page scope
                    <input
                      value={selectedTemplate.page_scope || ""}
                      onChange={(event) => patchTemplate(selectedTemplateIndex, { page_scope: event.target.value })}
                      className="mt-2 block w-full rounded-lg border border-theme-secondary px-3 py-2 text-sm font-normal"
                      placeholder="What one page covers (e.g. one page per book, reviews nested)"
                    />
                  </label>
                  <label className="mt-3 block text-sm font-medium">
                    Data source
                    <input
                      value={selectedTemplate.data_source || ""}
                      onChange={(event) => patchTemplate(selectedTemplateIndex, { data_source: event.target.value })}
                      className="mt-2 block w-full rounded-lg border border-theme-secondary px-3 py-2 text-sm font-normal"
                      placeholder="Dataset name"
                    />
                  </label>
                  <label className="mt-3 block text-sm font-medium">
                    Relates to
                    <input
                      value={selectedTemplate.relates_to || ""}
                      onChange={(event) => patchTemplate(selectedTemplateIndex, { relates_to: event.target.value })}
                      className="mt-2 block w-full rounded-lg border border-theme-secondary px-3 py-2 text-sm font-normal"
                      placeholder="How this links to other pages"
                    />
                  </label>
                </div>
              )}
            </FolderTabs>
          )}
        </section>

        <div className="mt-2 flex gap-3">
          {draftPlan?.hash && (
            <button
              type="button"
              disabled={generating}
              className="rounded-lg border border-theme-secondary px-5 py-3 font-semibold disabled:opacity-50"
              onClick={startOver}
            >
              Start over
            </button>
          )}
          <button
            type="button"
            disabled={generating || !name.trim() || !subdomain || subdomainStatus === "checking" || subdomainAvailable !== true}
            onClick={generate}
            className="min-w-0 flex-1 rounded-lg bg-theme-primary py-3 font-semibold text-theme-button disabled:opacity-50"
          >
            {generating ? "Starting generation…" : "Generate my site"}
          </button>
        </div>
      </div>
    </>
  );
};

const GeneratingStep = () => {
  const dispatch = useDispatch();
  const { error: toastError } = useToast();
  const project = useSelector((state) => state.project.currentProject);
  const [buildChange, setBuildChange] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const done =
    project?.status === "live"
    || project?.status === "ready"
    || buildChange?.status === "completed"
    || buildChange?.status === "published";
  const failed = buildChange?.status === "failed";
  const stopped = buildChange?.status === "canceled";
  const buildLog = currentAttempt(buildChange).logs;
  const progress = typeof project?.build_progress === "number"
    ? project.build_progress
    : null;

  useEffect(() => {
    if (!project?.hash || done || failed || stopped) return undefined;
    const refresh = () => {
      projectsService.getProject(project.hash, (response) => {
        dispatch(setCurrentProject(response.data));
      });
      changeRequestsService.list(project.hash, {}, (response) => {
        const items = Array.isArray(response.data) ? response.data : response.data.results;
        const build = items.find((item) => item.agent_type === "sitebuilder");
        if (build) setBuildChange(build);
      });
    };
    refresh();
    const timer = setInterval(refresh, 3000);
    return () => clearInterval(timer);
  }, [dispatch, done, failed, stopped, project?.hash]);

  const retryBuild = () => {
    if (!project?.hash || !buildChange?.hash || retrying) return;
    setRetrying(true);
    changeRequestsService.retry(
      project.hash,
      buildChange.hash,
      (response) => {
        setBuildChange(response.data);
        setRetrying(false);
      },
      (err) => {
        setRetrying(false);
        toastError(getApiErrorMessage(err, "Could not retry the build."));
      },
    );
  };

  if (failed || stopped) {
    return (
      <div className="py-10 text-center">
        <div className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-full bg-red-600 text-2xl text-white">!</div>
        <h1 className="text-2xl font-bold">{stopped ? "Site build stopped" : "Site build failed"}</h1>
        <p className="mt-3 text-sm opacity-60">{buildChange.status_message || project.status_message}</p>
        <AgentLog lines={buildLog} className="mx-auto mt-8 w-full text-left" />
        <button
          type="button"
          onClick={retryBuild}
          disabled={retrying}
          className="mt-7 inline-block rounded-lg bg-theme-primary px-7 py-3 font-semibold text-theme-button disabled:opacity-60"
        >
          {retrying ? "Retrying…" : "Try again"}
        </button>
        <Link
          to={sitePath(project, "changes/")}
          className="mt-4 block w-full text-sm font-semibold text-theme-primary"
        >
          See details in Changes
        </Link>
      </div>
    );
  }

  if (done) {
    return (
      <div className="py-16 text-center">
        <div className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-full bg-theme-secondary text-2xl text-theme-primary">✓</div>
        <h1 className="text-2xl font-bold">Your site is ready!</h1>
        <p className="mt-3 text-theme-primary">
          {project.production_url?.replace(/^https?:\/\//, "")}
        </p>
        <Link
          to={sitePath(project, "preview/")}
          className="mt-7 inline-block rounded-lg bg-theme-primary px-7 py-3 font-semibold text-theme-button"
        >
          View my site
        </Link>
        <Link
          to={sitePath(project, "changes/")}
          className="mt-4 block w-full text-sm font-semibold text-theme-primary"
        >
          See build in Changes
        </Link>
      </div>
    );
  }

  return (
    <div className="py-10 text-center">
      <div className="mb-2 flex items-center justify-center gap-3">
        <WeaveLoader size={72} />
        <p className="text-5xl font-bold tabular-nums tracking-tight">
          {progress != null ? `${progress}%` : "…"}
        </p>
      </div>
      <p className="mt-2 text-sm opacity-60">
        {project?.status_message ? (
          <span className="inline-flex items-baseline justify-center">
            {project.status_message.replace(/\.+$/, "")}
            <AnimatedDots />
          </span>
        ) : (
          project?.model_override
            ? "Takes 20-30 minutes depending on research needed"
            : "Takes 10-20 minutes depending on research needed"
        )}
      </p>
      <AgentLog lines={buildLog} live className="mx-auto mt-8 w-full text-left" />
      <Link to={sitePath(project, "changes/")} className="mt-8 inline-block text-sm font-semibold text-theme-primary">
        Follow along in Changes
      </Link>
    </div>
  );
};

const AnalysisLoader = ({ activeStep, hasTables }) => {
  const steps = hasTables ? ANALYSIS_STEPS_WITH_TABLES : ANALYSIS_STEPS_BRIEF_ONLY;
  return (
  <div className="py-8">
    <div className="mb-7 flex justify-center">
      <WeaveLoader size={72} />
    </div>
    <h1 className="text-center text-2xl font-bold">Designing your site plan</h1>
    <p className="mx-auto mt-2 max-w-md text-center text-sm opacity-60">
      {hasTables
        ? "We are studying a representative sample of your data and turning it into a useful site strategy."
        : "We are researching the space and turning it into a useful site strategy."}
    </p>
    <div className="mx-auto mt-8 max-w-md space-y-2">
      {steps.map(([title, description], index) => {
        const complete = index < activeStep;
        const current = index === activeStep;
        let markerClasses = "border-2 border-theme-secondary";
        if (current) markerClasses = "animate-pulse border-2 border-theme-primary text-theme-primary";
        if (complete) markerClasses = "bg-theme-primary text-theme-button";
        return (
          <div
            key={title}
            className={`flex gap-3 rounded-xl border p-4 transition-all duration-500 ${
              current ? "border-theme-primary bg-theme-secondary" : "border-theme-secondary"
            } ${index > activeStep ? "opacity-35" : ""}`}
          >
            <div
              className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold ${markerClasses}`}
            >
              {complete ? "✓" : index + 1}
            </div>
            <div>
              <p className="text-sm font-semibold">{title}</p>
              <p className="mt-0.5 text-xs opacity-60">{description}</p>
            </div>
          </div>
        );
      })}
    </div>
  </div>
  );
};

const Onboarding = () => (
  <Routes>
    <Route element={<OnboardingLayout />}>
      <Route index element={<Intro />} />
      <Route path="brief/" element={<BriefStep />} />
      <Route element={<RequireOnboardingDraft />}>
        <Route path="plan/" element={<PlanStep />} />
      </Route>
      <Route path="details/" element={<Navigate to="/onboarding/plan/" replace />} />
      <Route element={<RequireOnboardingProject />}>
        <Route path="generating/" element={<GeneratingStep />} />
        <Route path="complete/" element={<Navigate to="/onboarding/generating/" replace />} />
      </Route>
    </Route>
  </Routes>
);

const GiftChoice = ({ gift, busy, onRespond }) => {
  const isSite = gift.gift_type === "site";
  const name = gift.name || "a site";
  return (
    <div className="py-4 text-center">
      <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-theme-secondary text-2xl">🎁</div>
      <h1 className="text-2xl font-bold">{isSite ? "You've been gifted a site" : "You've been gifted an automated website plan"}</h1>
      <p className="mx-auto mt-3 max-w-md text-sm opacity-60">
        {isSite ? (
          <>{gift.gifter_email} gifted you <strong className="font-semibold text-theme-text">{name}</strong>. It's yours now, ready to view and grow.</>
        ) : (
          <>{gift.gifter_email} planned <strong className="font-semibold text-theme-text">{name}</strong> for you. Review the plan, then generate it.</>
        )}
      </p>
      <button
        type="button"
        disabled={busy}
        onClick={() => onRespond(true)}
        className="mt-8 w-full rounded-lg bg-theme-primary py-3 font-semibold text-theme-button disabled:opacity-50"
      >
        {isSite ? "View your site" : `Finish building ${name}`}
      </button>
      <button
        type="button"
        disabled={busy}
        onClick={() => onRespond(false)}
        className="mt-3 w-full rounded-lg border border-theme-secondary py-3 font-semibold disabled:opacity-50"
      >
        Build a new site instead
      </button>
    </div>
  );
};

const CsvEmptyArt = () => (
  <div className="relative h-16 w-28" aria-hidden="true">
    <span className="absolute left-1 top-3 rotate-[-10deg] rounded-lg border border-theme-secondary bg-theme-main p-2 shadow-sm">
      <svg viewBox="0 0 24 24" className="h-7 w-7 text-theme-text opacity-50" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M7 3.75h7.5L19.5 8.75v11.5a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4.75a1 1 0 0 1 1-1Z" />
        <path d="M14.25 3.75V8.5H19" />
        <path d="M8.5 12.5h7M8.5 15.5h7M8.5 18.5h4.5" />
      </svg>
    </span>
    <span className="absolute left-8 top-1 rotate-[4deg] rounded-lg border border-theme-secondary bg-theme-main p-2 shadow-md">
      <svg viewBox="0 0 24 24" className="h-8 w-8" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ color: "#5EEAD4" }}>
        <path d="M7 3.75h7.5L19.5 8.75v11.5a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4.75a1 1 0 0 1 1-1Z" />
        <path d="M14.25 3.75V8.5H19" />
        <path d="M9 13h2.2M9 16h6M9 19h4" />
      </svg>
    </span>
    <span className="absolute right-0 top-4 rotate-[12deg] rounded-lg border border-theme-secondary bg-theme-main p-2 shadow-sm">
      <svg viewBox="0 0 24 24" className="h-6 w-6 text-theme-text opacity-40" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M7 3.75h7.5L19.5 8.75v11.5a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4.75a1 1 0 0 1 1-1Z" />
        <path d="M14.25 3.75V8.5H19" />
        <path d="M8.75 13.5h6.5M8.75 16.5h4" />
      </svg>
    </span>
  </div>
);

const ErrorBanner = ({ children }) => (
  <div className="mb-5 rounded-lg bg-red-600 p-3 text-sm text-white">{children}</div>
);

function AnimatedDots() {
  const frames = [".", "..", "...", ""];
  const [step, setStep] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setStep((s) => (s + 1) % frames.length), 400);
    return () => clearInterval(id);
  }, []);
  return (
    <span aria-hidden="true" className="inline-block w-4 text-left">
      {frames[step]}
    </span>
  );
}

export default Onboarding;
